import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { api } from "../client";
import { qk } from "../keys";

/**
 * 모의훈련(드릴) — 서버 `drill` 앱(/api/v1/drill/…).
 * 시뮬레이터가 백엔드로 들어가면서 현장 노드에 값을 주입하는 주체는 서버다. 웹은
 * 시나리오 선택·시작·종료·진행 표시만 한다. 훈련 판독/경보는 `is_drill` 로 구분된다.
 */

export interface DrillScenario {
  id: number;
  code: string;
  name: string;
  description: string;
  duration_s: number;
  kpi_code: string;
  criteria: string;
  sort_order: number;
  is_active: boolean;
  targets: string[];
}

export type DrillStatus = "running" | "finished" | "aborted";

export interface DrillResult {
  alerts?: number;
  cleared_on_stop?: number;
  first_alert_at?: string | null;
  first_ack_at?: string | null;
  response_s?: number | null;
  max_level?: number;
  ticks?: number;
  readings_sent?: number;
  elapsed_s?: number;
  participants?: number | null;
  checked_in?: number;
  unevacuated?: number | null;
  evac_s?: number | null;
  last_checkin_at?: string | null;
}

export interface DrillSession {
  id: number;
  site: number;
  site_name: string;
  scenario: number;
  scenario_code: string;
  scenario_name: string;
  status: DrillStatus;
  started_by: number | null;
  started_by_name: string;
  started_at: string;
  ended_at: string | null;
  speed: number;
  tick_s: number;
  last_tick_at: string | null;
  ticks: number;
  readings_sent: number;
  note: string;
  participants: number | null;
  field_devices: boolean;
  /** 현장 매니져 훈련 발령 — 매니져가 시나리오를 제 파이프라인에 흘려 화면·경광등·방송이 실제처럼 반응(2026-09-15). */
  manager_drill?: boolean;
  /** 수동 채널 조작 {node_code: {gas: {level}|{value}}} — 다음 틱부터 시나리오 값 대신 적용. */
  overrides: Record<string, Record<string, { level?: number; value?: number }>>;
  result: DrillResult;
  kpi_evidence: number | null;
  duration_s: number;
  elapsed_s: number;
  progress: number;
}

interface DrillScenarioList {
  count?: number;
  next: string | null;
  previous: string | null;
  results: DrillScenario[];
}
interface DrillSessionList {
  count?: number;
  next: string | null;
  previous: string | null;
  results: DrillSession[];
}

/**
 * 시나리오 이름을 표시 언어로. 이름·설명은 서버 DB 에 한국어로만 있어 UI 언어를 바꿔도
 * 한글로 남았다(2026-09-11 지적). 코드가 고정 시드(S-1~4·데모 10)라 코드별 번역을
 * 클라이언트에 둔다(`widgets.training.scenario.<code>`). 번역이 없으면 서버 이름 그대로.
 * 설명·평가기준은 여전히 서버 한국어다 — 언어별 컬럼은 서버 쪽 결정.
 */
export function scenarioDisplayName(t: TFunction, code: string, fallback: string): string {
  return t(`widgets.training.scenario.${code}`, { defaultValue: fallback });
}

export function useDrillScenarios() {
  return useQuery({
    queryKey: qk.ops.drill.scenarios,
    queryFn: () =>
      api.get<DrillScenarioList>("/drill/scenarios/", { params: { page_size: 50 } }).then((r) => ({
        ...r.data,
        // 카탈로그 순서(S-1~4 → 데모 10종)는 sort_order 가 정본 — 서버 기본 정렬과 무관하게 고정.
        results: [...r.data.results].sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
      })),
    staleTime: 60_000,
  });
}

/** 현장의 진행 중 세션 — 5초 폴링(틱 주기와 같다). WS `drill.status` 가 오면 즉시 무효화. */
export function useActiveDrill(siteId: number | null | undefined) {
  return useQuery({
    queryKey: qk.ops.drill.active(siteId ?? undefined),
    queryFn: () =>
      api.get<{ session: DrillSession | null }>("/drill/sessions/active/", { params: { site: siteId } }).then((r) => r.data),
    enabled: siteId != null,
    refetchInterval: 5_000,
  });
}

export function useDrillSessions(params?: { site?: number; status?: DrillStatus; page_size?: number }) {
  return useQuery({
    queryKey: qk.ops.drill.sessions(params),
    queryFn: () => api.get<DrillSessionList>("/drill/sessions/", { params }).then((r) => r.data),
    enabled: params?.site != null,
  });
}

export function useStartDrill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { site: number; scenario: string | number; speed?: number; note?: string;
                         participants?: number | null; field_devices?: boolean; manager_drill?: boolean }) =>
      api.post<DrillSession>("/drill/sessions/start/", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.ops.drill.all }),
  });
}

export function useDrillOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; node?: string; gas?: string; level?: number; value?: number; clear?: boolean }) =>
      api.post<DrillSession>(`/drill/sessions/${id}/override/`, body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.ops.drill.all }),
  });
}

export function useStopDrill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, abort }: { id: number; abort?: boolean }) =>
      api.post<DrillSession>(`/drill/sessions/${id}/stop/`, abort ? { abort: true } : {}).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.ops.drill.all });
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    },
  });
}
